/**
 * ProfileRegistry.ts - Command Profile Registry
 * 
 * CHANGE HISTORY:
 * - 251116_2353 danielbeer: Refactor to follow Registr patterns
 * - 251116_2349 danielbeer: Create profile registry for managing command profiles
 * 
 * Central registry for command profiles with context resolution.
 * Follows OpCentral Registr standards for registry management.
 */

import type { 
  CommandProfile, 
  ResolvedProfile,
  ProfileRegistry as IProfileRegistry 
} from './types';

/**
 * Profile Registry Implementation
 * 
 * Follows Registr patterns:
 * - In-memory storage with Map
 * - CRUD operations (add, get, update, delete)
 * - Query operations (find, findOne, count)
 * - Validation
 * - Export/Import
 * 
 * Future: Can be backed by full Registr instance with schema validation
 */
export class ProfileRegistry implements IProfileRegistry {
  private profiles: Map<string, CommandProfile> = new Map();
  private schema = 'command-profile'; // Registr schema ID
  
  /**
   * Register a command profile
   */
  register(profile: CommandProfile): void {
    if (this.profiles.has(profile.id)) {
      console.warn(`[ProfileRegistry] Profile '${profile.id}' already registered, overwriting`);
    }
    
    this.profiles.set(profile.id, profile);
    console.log(`[ProfileRegistry] Registered profile: ${profile.id} (${profile.name})`);
  }
  
  /**
   * Get a profile by ID
   */
  get(profileId: string): CommandProfile | undefined {
    return this.profiles.get(profileId);
  }
  
  /**
   * Get all registered profiles
   */
  getAll(): CommandProfile[] {
    return Array.from(this.profiles.values());
  }
  
  /**
   * Resolve a profile with context overrides applied
   */
  resolve(profileId: string, context: string): ResolvedProfile | undefined {
    const profile = this.profiles.get(profileId);
    if (!profile) {
      console.warn(`[ProfileRegistry] Profile '${profileId}' not found`);
      return undefined;
    }
    
    // Get context overrides
    const contextOverrides = profile.contexts?.[context] || [];
    
    // Apply overrides to commands
    const commands = profile.commands.map(cmd => {
      // Find override for this command's action
      const override = contextOverrides.find(o => o.action === cmd.action);
      
      if (!override) {
        return cmd; // No override, return original
      }
      
      // Apply override
      return {
        ...cmd,
        label: override.label || cmd.label,
        description: override.description || cmd.description,
        // Note: handler is not part of CommandDefinition, handled by consumer
      };
    });
    
    return {
      profile,
      context,
      commands
    };
  }
  
  /**
   * Unregister a profile
   */
  unregister(profileId: string): void {
    if (this.profiles.delete(profileId)) {
      console.log(`[ProfileRegistry] Unregistered profile: ${profileId}`);
    } else {
      console.warn(`[ProfileRegistry] Profile '${profileId}' not found`);
    }
  }
  
  /**
   * Check if a profile is registered
   */
  has(profileId: string): boolean {
    return this.profiles.has(profileId);
  }
  
  /**
   * Clear all profiles
   */
  clear(): void {
    this.profiles.clear();
    console.log('[ProfileRegistry] Cleared all profiles');
  }
  
  /**
   * Get profile count
   */
  get size(): number {
    return this.profiles.size;
  }
  
  // ========================================
  // REGISTR-STYLE METHODS
  // ========================================
  
  /**
   * Add a profile (alias for register, Registr-style)
   */
  add(profile: CommandProfile): void {
    this.register(profile);
  }
  
  /**
   * Update a profile
   */
  update(profileId: string, updates: Partial<CommandProfile>): boolean {
    const profile = this.profiles.get(profileId);
    if (!profile) {
      console.warn(`[ProfileRegistry] Profile '${profileId}' not found`);
      return false;
    }
    
    const updated = { ...profile, ...updates, id: profileId }; // Preserve ID
    this.profiles.set(profileId, updated);
    console.log(`[ProfileRegistry] Updated profile: ${profileId}`);
    return true;
  }
  
  /**
   * Delete a profile (alias for unregister, Registr-style)
   */
  delete(profileId: string): boolean {
    const deleted = this.profiles.delete(profileId);
    if (deleted) {
      console.log(`[ProfileRegistry] Deleted profile: ${profileId}`);
    } else {
      console.warn(`[ProfileRegistry] Profile '${profileId}' not found`);
    }
    return deleted;
  }
  
  /**
   * Find profiles matching criteria
   */
  find(filter?: Partial<CommandProfile>): CommandProfile[] {
    if (!filter) {
      return Array.from(this.profiles.values());
    }
    
    return Array.from(this.profiles.values()).filter(profile => {
      return Object.entries(filter).every(([key, value]) => {
        return profile[key as keyof CommandProfile] === value;
      });
    });
  }
  
  /**
   * Find one profile matching criteria
   */
  findOne(filter: Partial<CommandProfile>): CommandProfile | undefined {
    return this.find(filter)[0];
  }
  
  /**
   * Count profiles matching criteria
   */
  count(filter?: Partial<CommandProfile>): number {
    return this.find(filter).length;
  }
  
  /**
   * Export all profiles (Registr-style)
   */
  export(): CommandProfile[] {
    return Array.from(this.profiles.values());
  }
  
  /**
   * Import profiles (Registr-style)
   */
  import(profiles: CommandProfile[]): void {
    profiles.forEach(profile => this.register(profile));
    console.log(`[ProfileRegistry] Imported ${profiles.length} profiles`);
  }
  
  /**
   * Get schema ID (Registr compatibility)
   */
  getSchema(): string {
    return this.schema;
  }
}

// Singleton instance
let instance: ProfileRegistry | null = null;

/**
 * Get the global profile registry instance
 */
export function getProfileRegistry(): ProfileRegistry {
  if (!instance) {
    instance = new ProfileRegistry();
  }
  return instance;
}
